import type { LanguageChoice } from '../../../i18n/languages';

export const SEVERITIES = ['EMERGENCY', 'MAJOR', 'MINOR', 'CAUTION'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const DELIVERY_MODES = ['NOW', 'DIGEST', 'OFF'] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export const LADDER_STEPS = ['OPENED', 'NOT_STARTED', 'OVERDUE', 'OVERDUE_2X', 'CHRONIC'] as const;
export type LadderStep = (typeof LADDER_STEPS)[number];

export const LADDER_STEPS_UI = [...LADDER_STEPS, 'UNKNOWN'] as const;
export type LadderStepUi = (typeof LADDER_STEPS_UI)[number];

export const FEED_STATES = ['ALERTED', 'HELD', 'WAITING'] as const;
export type FeedState = (typeof FEED_STATES)[number];

export const REALTIME_CHANNELS = ['WHATSAPP', 'SMS'] as const;
export type RealtimeChannel = (typeof REALTIME_CHANNELS)[number];

export const ROLE_GROUPS = ['OPERATOR', 'LEAD', 'SENIOR_LEAD', 'REGULAR_NON_OP', 'SENIOR_NON_OP'] as const;
export type RoleGroup = (typeof ROLE_GROUPS)[number];

export const ONBOARDING_STATUSES = ['PENDING', 'COMPLETED', 'SKIPPED'] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export const ONBOARDING_ENTRIES = ['INVITE', 'EXISTING'] as const;
export type OnboardingEntry = (typeof ONBOARDING_ENTRIES)[number];

export type OnboardingOutcome = Exclude<OnboardingStatus, 'PENDING'>;

export interface OnboardingState {
  readonly status: OnboardingStatus;
  readonly at: string | null;
  readonly entry: OnboardingEntry;
}

export const QUIET_HOURS_SOURCES = ['PERSONAL', 'SITE_DEFAULT'] as const;
export type QuietHoursSource = (typeof QUIET_HOURS_SOURCES)[number];

export const DIGEST_WHEN = ['ALWAYS', 'OVERDUE', 'CHRONIC', 'NEVER'] as const;
export type DigestWhen = (typeof DIGEST_WHEN)[number];

export const MANAGEABLE_REASONS = ['PHONE_NOT_VERIFIED', 'NO_CONTACT', 'UNKNOWN_ROLE'] as const;
export type ManageableReason = (typeof MANAGEABLE_REASONS)[number];

export interface PushState {
  readonly promptedAt: string | null;
  readonly devices: number;
}

export const ALWAYS_REACHES_KEYS = [
  'HANDED_TO_YOU',
  'APPROVAL_DECIDED',
  'APPROVALS_WAITING',
  'HELP_REQUESTS',
  'EMERGENCY',
  'EMERGENCY_UNLESS_TURNED_DOWN',
] as const;
export type AlwaysReachesKey = (typeof ALWAYS_REACHES_KEYS)[number];

export type SeverityModes = Readonly<Record<Severity, DeliveryMode>>;

export type SaveOutcome =
  | { readonly kind: 'SAVED' }
  | { readonly kind: 'MAJOR_DROPPED'; readonly to: Exclude<DeliveryMode, 'NOW'> }
  | { readonly kind: 'QUIET_DROPPED' };

export interface ModeChoice {
  readonly mode: DeliveryMode;
  readonly disabled: boolean;
}

export interface ModeChoices {
  readonly options: readonly ModeChoice[];
  readonly locked: boolean;
}
export type AllowedModes = Readonly<Record<Severity, readonly DeliveryMode[]>>;

export interface QuietWindow {
  readonly startMinute: number;
  readonly endMinute: number;
}

export const HEAR_LINES = [
  'ISSUES_AND_TASKS',
  'NOT_STARTED_REMINDERS',
  'HANDED_TO_YOU',
  'ISSUES_AND_REMINDERS',
  'APPROVALS_HANDOFFS_HELP',
  'OPERATOR_TURNS_DOWN',
  'EMERGENCIES_AT_ONCE',
  'ANYTHING_OVERDUE',
  'APPROVALS_OVERDUE',
  'OVERDUE_IN_MORNING_EMAIL',
  'CHRONIC_IN_MORNING_EMAIL',
] as const;
export type HearLine = (typeof HEAR_LINES)[number];

export interface QuietState {
  readonly active: boolean;
  readonly until: string | null;
  readonly startsAt: string | null;
}

export interface SiteQuietHours {
  readonly plantId: string;
  readonly plantName: string;
  readonly quietHoursEnabled: boolean;
  readonly quietHours: QuietWindow;
  readonly quiet: QuietState;
}

export interface SettingsLanguage {
  readonly saved: LanguageChoice | null;
  readonly effective: LanguageChoice;
  readonly offered: readonly LanguageChoice[];
  readonly siteDefault: LanguageChoice;
}

export const CHANGE_NOTICES = ['REALTIME', 'DIGEST'] as const;
export type ChangeNotice = (typeof CHANGE_NOTICES)[number];
export type ChangeNotices = Readonly<Record<Severity, Readonly<Partial<Record<DeliveryMode, ChangeNotice>>>>>;

export interface HandoffStall {
  readonly handoffs: number;
  readonly withinHours: number;
}

export interface NoOperatorPlant {
  readonly plantId: string;
  readonly plantName: string;
}

export interface MySettings {
  readonly userId: string;
  readonly roleKey: string;
  readonly roleGroup: RoleGroup | null;
  readonly isOperational: boolean;
  readonly receivesHandoffAlerts: boolean;
  readonly manageable: boolean;
  readonly modes: SeverityModes;
  readonly allowed: AllowedModes;
  readonly quietHours: QuietWindow;
  readonly quietHoursEnabled: boolean;
  readonly alwaysReaches: readonly AlwaysReachesKey[];
  readonly phoneVerified: boolean;
  readonly emailVerified: boolean;
  readonly realtimeChannel: RealtimeChannel;
  readonly handoffAlerts: boolean;
  readonly handoffStall: HandoffStall | null;
  readonly onboarding: OnboardingState;
  readonly quietHoursSource: QuietHoursSource;
  readonly push: PushState;
  readonly digestWhen: Readonly<Record<Severity, DigestWhen>>;
  readonly manageableReason: ManageableReason | null;
  readonly quiet: QuietState;
  readonly reachesOn: RealtimeChannel | null;
  readonly switchTo: RealtimeChannel | null;
  readonly hears: readonly HearLine[];
  readonly changeNotices: ChangeNotices;
  readonly digestAtMinute: number;
  readonly noOperatorAt: readonly NoOperatorPlant[];
  readonly siteQuietHours: readonly SiteQuietHours[];
  readonly language: SettingsLanguage | null;
}

export interface SettingsPatch {
  readonly modes?: Partial<SeverityModes>;
  readonly quietHours?: QuietWindow | null;
  readonly quietHoursEnabled?: boolean;
  readonly handoffAlerts?: boolean;
  readonly pushPromptedAt?: string;
}

export const TOLD_ROLES = ['LEAD', 'SITE_ADMIN'] as const;
export type ToldRole = (typeof TOLD_ROLES)[number];

export interface ToldAudience {
  readonly leads: readonly string[];
  readonly siteAdmins: readonly string[];
}

export interface CoverageNotice {
  readonly severity: 'MAJOR' | 'MINOR' | null;
  readonly realtime: boolean;
  readonly told: readonly string[];
  readonly audience: ToldAudience | null;
  readonly delivered: boolean;
}

export interface SettingsSaved {
  readonly settings: MySettings;
  readonly notified: CoverageNotice | null;
}

export interface SettingsViolation {
  readonly severity: Severity;
  readonly mode: DeliveryMode;
  readonly allowed: readonly DeliveryMode[];
}

export const ISSUE_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const READING_TRENDS = ['RISING', 'FALLING', 'STEADY'] as const;
export type ReadingTrend = (typeof READING_TRENDS)[number];

export interface IssueReading {
  readonly value: number;
  readonly unit: string;
  readonly band: string | null;
  readonly trend: ReadingTrend | null;
}

export interface StartedBy {
  readonly userId: string;
  readonly name: string;
  readonly at: string;
}

export const HOOTER_STATES = ['SOUNDING', 'SILENCED', 'UNACKNOWLEDGED', 'OFF'] as const;
export type HooterState = (typeof HOOTER_STATES)[number];

export interface HooterStatus {
  readonly state: HooterState;
  readonly calling: readonly string[];
}

export const NEXT_ACTIONS = ['START', 'START_AND_SILENCE', 'TAKE_OVER'] as const;
export type NextAction = (typeof NEXT_ACTIONS)[number];

export interface FeedItem {
  readonly issueId: string | null;
  readonly plantId: string;
  readonly severity: Severity;
  readonly step: LadderStepUi;
  readonly title: string;
  readonly at: string;
  readonly state: FeedState;
  readonly link: string | null;
  readonly issueTitle: string | null;
  readonly plantName: string | null;
  readonly location: string | null;
  readonly reading: IssueReading | null;
  readonly startedBy: StartedBy | null;
  readonly status: IssueStatus | null;
  readonly hooter: HooterStatus | null;
  readonly fullScreenAlarm: boolean;
  readonly nextAction: NextAction | null;
}

export interface QuietSummary {
  readonly active: boolean;
  readonly until: string | null;
  readonly startsAt: string | null;
  readonly held: Readonly<Record<'MINOR' | 'CAUTION', number>>;
}

export interface GroupedFeed {
  readonly alerted: readonly FeedItem[];
  readonly held: readonly FeedItem[];
  readonly waiting: readonly FeedItem[];
}

export interface FeedPage {
  readonly items: readonly FeedItem[];
  readonly skipped: number;
  readonly quiet: QuietSummary;
}

export interface HooterDurations {
  readonly minSec: number;
  readonly maxSec: number;
  readonly presetsSec: readonly number[];
}

export interface PlantPolicy {
  readonly plantId: string;
  readonly hooterEnabled: boolean;
  readonly hooterDurationSec: number;
  readonly hooterDurations: HooterDurations;
  readonly quietHours: QuietWindow;
  readonly quietHoursEnabled: boolean;
  readonly languages: readonly RegionalLanguage[];
  readonly defaultLanguage: RegionalLanguage;
  readonly supportedLanguages: readonly RegionalLanguage[];
}

export interface PlantPolicyPatch {
  readonly hooterEnabled?: boolean;
  readonly hooterDurationSec?: number;
  readonly quietHours?: QuietWindow;
  readonly quietHoursEnabled?: boolean;
  readonly languages?: readonly RegionalLanguage[];
  readonly defaultLanguage?: RegionalLanguage;
}

export type PolicyBlock = 'quietHours' | 'hooter' | 'regional';

export const COVERAGE_GAPS = [
  'NO_OPERATOR_WITH_VERIFIED_PHONE',
  'NO_LEAD_WITH_VERIFIED_PHONE',
  'NO_OPERATOR_HEARS_MAJOR',
  'NO_LEAD_HEARS_MAJOR',
] as const;
export type CoverageGap = (typeof COVERAGE_GAPS)[number];

export const ROUTING_BUCKETS = ['OPERATOR', 'LEAD', 'SENIOR_LEAD', 'CLIENT', 'SITE_ADMIN'] as const;
export type RoutingBucket = (typeof ROUTING_BUCKETS)[number];

export interface RoutingPerson {
  readonly userId: string;
  readonly name: string;
  readonly roleKey: string;
  readonly bucket: RoutingBucket | null;
  readonly inAppOnly: boolean;
}

export interface SeverityRouting {
  readonly severity: Severity;
  readonly toldNow: readonly RoutingPerson[];
  readonly morningEmail: readonly RoutingPerson[];
  readonly morningEmailWhenOverdue: readonly RoutingPerson[];
  readonly callCount: number;
  readonly hooterSeconds: number | null;
  readonly fullScreenAlarm: boolean;
  readonly overdueAfterMinutes: number | null;
  readonly realtimeExpected: boolean;
}

export const GAP_REASONS = ['PHONE_UNVERIFIED', 'NO_PHONE', 'MAJOR_SET_TO_DIGEST', 'MAJOR_SET_TO_OFF'] as const;
export type GapReason = (typeof GAP_REASONS)[number];

export interface GapPerson {
  readonly userId: string;
  readonly name: string;
  readonly roleGroup: RoleGroup | null;
  readonly reason: GapReason;
}

export interface GapDetail {
  readonly gap: CoverageGap;
  readonly people: readonly GapPerson[];
}

export interface PlantHealth {
  readonly ok: boolean;
  readonly gaps: readonly CoverageGap[];
  readonly blocksRosterSave: boolean;
  readonly gapDetails: readonly GapDetail[];
}

export interface RoutingFailure {
  readonly userId: string;
  readonly name: string;
  readonly channel: string;
  readonly count: number;
  readonly switchTo: RealtimeChannel | null;
}

export interface PlantRouting {
  readonly plantId: string;
  readonly health: PlantHealth;
  readonly severities: readonly SeverityRouting[];
  readonly quietHours: { readonly usingDefault: number; readonly total: number };
  readonly failures: { readonly since: string; readonly people: number; readonly byPerson: readonly RoutingFailure[] };
}

export const START_SOURCES = ['APP', 'LANDING'] as const;
export type StartSource = (typeof START_SOURCES)[number];

export interface IssueHolding {
  readonly startedBy: StartedBy | null;
  readonly status: IssueStatus | null;
  readonly nextAction: NextAction | null;
}

export type IssueStartResult =
  | ({ readonly outcome: 'STARTED'; readonly hooterSilenced: boolean } & IssueHolding)
  | ({ readonly outcome: 'ALREADY_STARTED'; readonly hooterSilenced: boolean } & IssueHolding)
  | { readonly outcome: 'NOT_FOUND' };

export type IssueTakeOverResult =
  | ({ readonly outcome: 'TAKEN_OVER'; readonly previousOwner: string | null } & IssueHolding)
  | ({ readonly outcome: 'ALREADY_YOURS' } & IssueHolding)
  | { readonly outcome: 'NOT_STARTED' }
  | { readonly outcome: 'NOT_FOUND' };

export const LANDING_LANGUAGES = ['en', 'hi'] as const;
export type LandingLanguage = (typeof LANDING_LANGUAGES)[number];

export interface LandingHooter {
  readonly sounding: boolean;
  readonly callsPlaced: number;
}

export interface LandingResolution {
  readonly at: string;
  readonly byName: string;
}

export interface LandingRecipient {
  readonly phoneMasked: string;
}

export interface LandingRecord {
  readonly issueId: string;
  readonly severity: Severity;
  readonly title: string;
  readonly plantName: string;
  readonly area: string | null;
  readonly equipment: string | null;
  readonly firedAt: string;
  readonly reading: IssueReading | null;
  readonly status: IssueStatus;
  readonly nextAction: NextAction | null;
  readonly startedBy: StartedBy | null;
  readonly resolved: LandingResolution | null;
  readonly hooter: LandingHooter | null;
  readonly language: LandingLanguage;
  readonly channel: RealtimeChannel;
  readonly recipient: LandingRecipient;
  readonly appPath: string | null;
}

export type LandingStartResult =
  | ({ readonly kind: 'STARTED'; readonly hooterSilenced: boolean; readonly appPath: string | null } & IssueHolding)
  | ({ readonly kind: 'ALREADY_STARTED' } & IssueHolding)
  | { readonly kind: 'NOT_OPEN' };

export type LandingTakeOverResult =
  | ({ readonly kind: 'TAKEN_OVER'; readonly appPath: string | null } & IssueHolding)
  | ({ readonly kind: 'ALREADY_YOURS'; readonly appPath: string | null } & IssueHolding)
  | { readonly kind: 'NOT_STARTED' }
  | { readonly kind: 'NOT_OPEN' };

export interface PushSubscriptionBody {
  readonly endpoint: string;
  readonly keys: { readonly p256dh: string; readonly auth: string };
  readonly expirationTime: number | null;
  readonly device: { readonly userAgent: string; readonly installed: boolean };
}

export interface PushSubscriptionSaved {
  readonly id: string;
  readonly createdAt: string;
}

export interface PushText {
  readonly title: string;
  readonly body: string;
  readonly start: string;
  readonly open: string;
}

export interface PushPayload {
  readonly v: 1;
  readonly issueId: string | null;
  readonly plantId: string;
  readonly severity: Severity;
  readonly step: LadderStepUi;
  readonly at: string;
  readonly link: string | null;
  readonly lang: LandingLanguage;
  readonly issueTitle: string;
  readonly plantName: string;
  readonly location: string | null;
  readonly reading: IssueReading | null;
  readonly startedBy: StartedBy | null;
  readonly hooter: HooterStatus | null;
  readonly text: PushText;
}

export interface PersonChannel {
  readonly userId: string;
  readonly realtimeChannel: RealtimeChannel;
}

export type NotificationSignal =
  | { readonly kind: 'feed' }
  | { readonly kind: 'settings' }
  | { readonly kind: 'issue'; readonly issueId: string };

export type NotificationSignalKind = NotificationSignal['kind'];

export interface PlantProfile {
  readonly plantId: string;
  readonly name: string;
  readonly code: string;
  readonly address: string | null;
  readonly capacityKld: number | null;
  readonly timezone: string | null;
}

export interface PlantProfilePatch {
  readonly name?: string;
  readonly address?: string;
  readonly capacityKld?: number | null;
  readonly timezone?: string;
}

export const REGIONAL_LANGUAGES = LANDING_LANGUAGES;
export type RegionalLanguage = LandingLanguage;

export interface RegionalSettings {
  readonly plantId: string;
  readonly languages: readonly RegionalLanguage[];
  readonly defaultLanguage: RegionalLanguage;
  readonly supportedLanguages: readonly RegionalLanguage[];
}

export interface RegionalSettingsPatch {
  readonly languages?: readonly RegionalLanguage[];
  readonly defaultLanguage?: RegionalLanguage;
}
